import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'ht_access_token';
const REFRESH_KEY = 'ht_refresh_token';

const AuthContext = createContext(null);

// Token helpers
function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function getStoredRefreshToken() {
  return localStorage.getItem(REFRESH_KEY);
}

function storeTokens(accessToken, refreshToken) {
  if (accessToken) localStorage.setItem(TOKEN_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
}

function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

// Set up axios request interceptor to attach token
axios.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);
  const isRefreshing = useRef(false);
  const failedQueue = useRef([]);

  const processQueue = (error, newToken = null) => {
    failedQueue.current.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(newToken);
      }
    });
    failedQueue.current = [];
  };

  // Setup axios response interceptor for automatic token refresh
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
          const refreshToken = getStoredRefreshToken();
          if (!refreshToken) {
            setUser(false);
            clearTokens();
            return Promise.reject(error);
          }

          if (isRefreshing.current) {
            return new Promise((resolve, reject) => {
              failedQueue.current.push({ resolve, reject });
            }).then((newToken) => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axios(originalRequest);
            }).catch(err => Promise.reject(err));
          }

          originalRequest._retry = true;
          isRefreshing.current = true;

          try {
            const { data } = await axios.post(`${API_URL}/api/auth/refresh`, {
              refresh_token: refreshToken
            });
            const newToken = data.access_token;
            storeTokens(newToken, null);
            processQueue(null, newToken);
            isRefreshing.current = false;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError);
            isRefreshing.current = false;
            setUser(false);
            clearTokens();
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const checkAuth = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    
    // Add timeout to prevent infinite loading
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, { signal: controller.signal });
      setUser({ ...data, id: data._id || data.id });
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        // Timeout - clear auth and let user login again
        setUser(false);
        clearTokens();
      } else if (error.response?.status === 401) {
        // Token expired - try refresh once
        const refreshToken = getStoredRefreshToken();
        if (refreshToken) {
          try {
            const { data: refreshData } = await axios.post(`${API_URL}/api/auth/refresh`, {
              refresh_token: refreshToken
            });
            storeTokens(refreshData.access_token, null);
            // Use the user data from refresh response if available, otherwise fetch /me
            if (refreshData.id && refreshData.email) {
              setUser({ id: refreshData.id, email: refreshData.email, name: refreshData.name, plan: refreshData.plan });
            } else {
              const { data } = await axios.get(`${API_URL}/api/auth/me`);
              setUser({ ...data, id: data._id || data.id });
            }
          } catch {
            setUser(false);
            clearTokens();
          }
        } else {
          setUser(false);
          clearTokens();
        }
      } else {
        setUser(false);
        clearTokens();
      }
    } finally {
      clearTimeout(timeout);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    storeTokens(data.access_token, data.refresh_token);
    const userData = { id: data.id, email: data.email, name: data.name, plan: data.plan };
    setUser(userData);
    setLoading(false);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await axios.post(`${API_URL}/api/auth/register`, { email, password, name });
    storeTokens(data.access_token, data.refresh_token);
    const userData = { id: data.id, email: data.email, name: data.name, plan: data.plan };
    setUser(userData);
    setLoading(false);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch {
      // ignore errors on logout
    }
    clearTokens();
    setUser(false);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout,
      refreshUser,
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
